/**
 * BillingSection — facturatiegegevens card with view/edit modes.
 *
 * Read-only by default; "Bewerken" enters edit mode where users fill
 * name + address fields and pick a country from a short EU allowlist.
 * Payment method is a placeholder (handled later).
 */

import { useCallback, useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import {
  COUNTRY_LABEL,
  EMPTY_BILLING,
  EU_COUNTRIES,
  isBillingEmpty,
  type BillingDetails,
} from "@/routes/account/billing/types";

export function BillingSection() {
  const [data, setData] = useState<BillingDetails>(EMPTY_BILLING);
  const [draft, setDraft] = useState<BillingDetails>(EMPTY_BILLING);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const fetched = await authFetchJson<BillingDetails>("/api/account/billing");
        setData(fetched);
        setDraft(fetched);
      } catch {
        // Silent on initial load — defaults are usable and a real
        // error will surface on save if the API is unreachable.
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const startEdit = useCallback(() => {
    setDraft(data);
    setError("");
    setEditing(true);
  }, [data]);

  const cancel = useCallback(() => {
    setDraft(data);
    setError("");
    setEditing(false);
  }, [data]);

  const update = useCallback(
    <K extends keyof BillingDetails>(key: K, value: BillingDetails[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const save = useCallback(async () => {
    setSaving(true);
    setError("");
    try {
      const saved = await authFetchJson<BillingDetails>("/api/account/billing", {
        method: "PUT",
        body: JSON.stringify(draft),
      });
      setData(saved);
      setDraft(saved);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }, [draft]);

  if (loading) {
    return (
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-slate-900">Facturatiegegevens</h2>
        <div className="space-y-2">
          <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
        </div>
      </section>
    );
  }

  return (
    <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">Facturatiegegevens</h2>
        {!editing ? (
          <button
            type="button"
            onClick={startEdit}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Bewerken
          </button>
        ) : null}
      </div>

      {!editing ? (
        <ViewMode data={data} />
      ) : (
        <EditMode
          draft={draft}
          update={update}
          saving={saving}
          error={error}
          onCancel={cancel}
          onSave={save}
        />
      )}

      <PaymentMethodPlaceholder />
    </section>
  );
}

function ViewMode({ data }: { data: BillingDetails }) {
  if (isBillingEmpty(data)) {
    return <p className="text-sm text-slate-500">Nog geen facturatiegegevens ingevuld.</p>;
  }

  const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ");
  const streetLine = [data.street, data.house_number].filter(Boolean).join(" ");
  const cityLine = [data.postal_code, data.city].filter(Boolean).join(" ");
  const countryName = COUNTRY_LABEL[data.country] ?? data.country;

  return (
    <div className="text-sm text-slate-900">
      {fullName ? <p className="font-medium">{fullName}</p> : null}
      <div className="mt-1 space-y-0.5 text-slate-700">
        {streetLine ? <p>{streetLine}</p> : null}
        {cityLine ? <p>{cityLine}</p> : null}
        <p className="text-slate-500">{countryName}</p>
      </div>
    </div>
  );
}

type UpdateFn = <K extends keyof BillingDetails>(key: K, value: BillingDetails[K]) => void;

function EditMode({
  draft,
  update,
  saving,
  error,
  onCancel,
  onSave,
}: {
  draft: BillingDetails;
  update: UpdateFn;
  saving: boolean;
  error: string;
  onCancel: () => void;
  onSave: () => void;
}) {
  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100";
  const labelClass = "block text-xs font-medium text-slate-500";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={labelClass}>Voornaam</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.first_name ?? ""}
            onChange={(e) => update("first_name", e.target.value)}
            autoComplete="given-name"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Achternaam</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.last_name ?? ""}
            onChange={(e) => update("last_name", e.target.value)}
            autoComplete="family-name"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
        <label className="block">
          <span className={labelClass}>Straat</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.street ?? ""}
            onChange={(e) => update("street", e.target.value)}
            autoComplete="address-line1"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Huisnr.</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.house_number ?? ""}
            onChange={(e) => update("house_number", e.target.value)}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
        <label className="block">
          <span className={labelClass}>Postcode</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.postal_code ?? ""}
            onChange={(e) => update("postal_code", e.target.value)}
            autoComplete="postal-code"
            inputMode="numeric"
          />
        </label>
        <label className="block">
          <span className={labelClass}>Plaats</span>
          <input
            type="text"
            className={`mt-1 ${inputClass}`}
            value={draft.city ?? ""}
            onChange={(e) => update("city", e.target.value)}
            autoComplete="address-level2"
          />
        </label>
      </div>

      <label className="block">
        <span className={labelClass}>Land</span>
        <select
          className={`mt-1 ${inputClass} appearance-none bg-white pr-8`}
          value={draft.country}
          onChange={(e) => update("country", e.target.value)}
          autoComplete="country"
        >
          {EU_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Annuleer
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
        >
          {saving ? "Opslaan…" : "Opslaan"}
        </button>
      </div>
    </div>
  );
}

function PaymentMethodPlaceholder() {
  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Betaalmethode
          </p>
          <p className="mt-0.5 text-sm italic text-slate-500">Wordt later toegevoegd</p>
        </div>
        <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
          Binnenkort
        </span>
      </div>
    </div>
  );
}
